<template>
  <div class="flex w-full min-w-0 max-w-full flex-col gap-4">
    <!-- Narrow screens: no milestones in the first three months of the window → show hint only -->
    <p
      v-if="showMobileTimelineDesktopMessageOnly"
      class="m-0 text-xs leading-snug text-[var(--color-inactive-dark)] sm:hidden"
    >
      {{ $t('ProgressPage.timelineDesktopOnlyHint') }}
    </p>

    <template v-else>
      <!-- Legend -->
      <ul
        class="m-0 flex list-none flex-wrap items-center justify-center gap-x-6 gap-y-2 p-0 text-sm text-[var(--color-black)]"
        aria-label="Timeline legend"
      >
        <li v-if="showNextLevelMarkers" class="flex items-center gap-2">
          <i class="pi pi-star-fill text-base text-[var(--color-black)]" aria-hidden="true" />
          <span>{{ $t('PersonalJourney.legend.nextLevel') }}</span>
        </li>
        <li v-if="showPersonalGoalsOnTimeline" class="flex items-center gap-2">
          <i class="pi pi-flag-fill text-base text-[var(--color-black)]" aria-hidden="true" />
          <span>{{ $t('PersonalJourney.legend.personalGoal', { text: featureNamePersonalGoals }) }}</span>
        </li>
        <li v-if="showWorkshopOnTimeline" class="flex items-center gap-2">
          <i class="pi pi-users text-base text-[var(--color-black)]" aria-hidden="true" />
          <span>{{ $t('PersonalJourney.legend.workshop', { text: featureName }) }}</span>
        </li>
      </ul>

      <p
        v-if="hasBeyondWindowItems"
        class="m-0 mb-2 text-xs text-[var(--color-inactive-dark)]"
      >
        {{ $t('ProgressPage.timelineBeyondWindowHint') }}
      </p>

      <div class="w-full min-w-0 max-w-full overflow-visible" :style="timelinePadStyle">
        <div
          class="pj-roadmap-months flex w-full min-w-0 max-w-full flex-row flex-nowrap gap-x-0.5 overflow-x-visible py-1 sm:gap-x-0 sm:py-0"
        >
          <PersonalJourneyOneMonthTimelineComponent
            v-for="(monthData, index) in displayMonthDataSet"
            :key="`${monthData.year}-${monthData.month}-${index}`"
            :month="monthData.month"
            :year="monthData.year"
            :events="monthData.events"
            class="min-w-0 max-w-full flex-[1_1_0] basis-0 overflow-visible"
            @updateMaxYPos="updateMaxYPos"
          />
        </div>
      </div>

      <p
        v-if="showDesktopOnlyMessage"
        class="m-0 text-xs leading-snug text-[var(--color-inactive-dark)] sm:hidden"
      >
        {{ $t('ProgressPage.timelineDesktopOnlyHint') }}
      </p>
    </template>
  </div>
</template>

<script>
import PersonalJourneyOneMonthTimelineComponent from './PersonalJourneyOneMonthTimelineComponent.vue';
import { getFeatureDisplayNameOrFallback } from '@/utils/featureDisplay';

/** Matches Tailwind `sm:` breakpoint (roadmap switches to full 6 months at this width). */
const MOBILE_MAX_WIDTH_PX = 639;

export default {
  name: 'PersonalJourneyTimelineComponent',
  components: {
    PersonalJourneyOneMonthTimelineComponent,
  },
  props: {
    events: {
      type: Array,
      required: true,
      default: () => [],
    },
    /** When true, hide projected next-level stars and legend (user is on the highest configured level). */
    maximumLevelReached: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      maxYPos: 0,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
    };
  },
  computed: {
    featureName() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'workshops',
        this.$t('FallbackTexts.workshop'),
        this.$i18n?.locale,
      );
    },
    featureNamePersonalGoals() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'personalGoals',
        this.$t('FallbackTexts.personalGoals'),
        this.$i18n?.locale,
      );
    },
    isMobileRoadmap() {
      return this.windowWidth <= MOBILE_MAX_WIDTH_PX;
    },
    showNextLevelOnTimeline() {
      return this.$store.getters.getShowNextLevelOnTimeline;
    },
    /** Store toggle and not at max level — no next level to reach. */
    showNextLevelMarkers() {
      if (this.maximumLevelReached) return false;
      return this.showNextLevelOnTimeline;
    },
    showPersonalGoalsOnTimeline() {
      return this.$store.getters.getShowPersonalGoalsOnTimeline;
    },
    showWorkshopOnTimeline() {
      return this.$store.getters.getShowWorkshopOnTimeline;
    },
    showNewFeatureOnTimeline() {
      return this.$store.getters.getShowNewFeatureOnTimeline;
    },
    futureGoal() {
      return this.$store.getters.getNextGoalOverflow;
    },
    futureNextLvl() {
      return this.$store.getters.getNextLvlOverflow;
    },
    /** Level overflow bubble only when milestones are still relevant. */
    effectiveNextLvlOverflow() {
      if (this.maximumLevelReached) return null;
      return this.futureNextLvl;
    },
    futureNextNewFeat() {
      return this.$store.getters.getNextNewFeatOverflow;
    },
    shouldApplyNextEventsPadding() {
      return [this.futureGoal, this.effectiveNextLvlOverflow, this.futureNextNewFeat].filter(Boolean).length === 2;
    },
    hasBeyondWindowItems() {
      return !!(this.effectiveNextLvlOverflow || this.futureNextNewFeat || this.futureGoal);
    },
    timelinePadStyle() {
      if (this.maxYPos <= 0) return {};
      return { paddingBottom: `${16 + this.maxYPos * 56}px` };
    },
    /** Events that may appear on the roadmap (respects legend toggles). */
    eligibleTypedEvents() {
      return this.events.filter((event) => {
        const t = event.type;
        return (
          (t === 'level' && this.showNextLevelMarkers)
          || (t === 'personal-goal' && this.showPersonalGoalsOnTimeline)
          || (t === 'workshop' && this.showWorkshopOnTimeline)
          || (t === 'new-feature' && this.showNewFeatureOnTimeline)
        );
      });
    },
    monthDataSet() {
      const today = new Date();
      const monthDataSet = [];
      for (let i = 0; i < 6; i += 1) {
        const futureMonth = new Date(today.getFullYear(), today.getMonth() + i, 1);
        const monthIndex = futureMonth.getMonth();
        const year = futureMonth.getFullYear();
        const monthlyEvents = this.events.filter((event) => {
          const eventDate = new Date(event.date);
          const eventMonth = eventDate.getMonth();
          const eventYear = eventDate.getFullYear();
          const eventType = event.type;
          const sameMonth = eventYear === year && eventMonth === monthIndex;
          return (
            sameMonth
            && (
              (eventType === 'level' && this.showNextLevelMarkers)
              || (eventType === 'personal-goal' && this.showPersonalGoalsOnTimeline)
              || (eventType === 'workshop' && this.showWorkshopOnTimeline)
              || (eventType === 'new-feature' && this.showNewFeatureOnTimeline)
            )
          );
        });
        monthDataSet.push({
          month: monthIndex,
          year,
          events: monthlyEvents,
        });
      }
      return monthDataSet;
    },
    /**
     * Index (0–5) of the six-month window that contains the chronologically first milestone.
     * If none fall in the window, 0 (current month only on narrow screens).
     */
    firstEventWindowIndex() {
      const today = new Date();
      const slots = Array.from({ length: 6 }, (_, i) => {
        const fm = new Date(today.getFullYear(), today.getMonth() + i, 1);
        return {
          index: i,
          month: fm.getMonth(),
          year: fm.getFullYear(),
        };
      });
      const eligible = this.eligibleTypedEvents;
      let bestTime = null;
      let bestIdx = 0;
      eligible.forEach((ev) => {
        const d = new Date(ev.date);
        const slot = slots.find(
          (s) => s.month === d.getMonth() && s.year === d.getFullYear(),
        );
        if (!slot) return;
        const t = d.getTime();
        if (bestTime === null || t < bestTime) {
          bestTime = t;
          bestIdx = slot.index;
        }
      });
      return bestTime === null ? 0 : bestIdx;
    },
    /** Calendar months at indices 0–2 of the six-month horizon (today → +2 months). */
    firstThreeWindowMonthKeys() {
      const today = new Date();
      return [0, 1, 2].map((i) => {
        const fm = new Date(today.getFullYear(), today.getMonth() + i, 1);
        return { month: fm.getMonth(), year: fm.getFullYear() };
      });
    },
    /** Any roadmap-visible event (per legend toggles) in the first three months of the window. */
    hasAnyEligibleEventInFirstThreeWindowMonths() {
      const slots = this.firstThreeWindowMonthKeys;
      return this.eligibleTypedEvents.some((ev) => {
        const d = new Date(ev.date);
        return slots.some(
          (s) => s.month === d.getMonth() && s.year === d.getFullYear(),
        );
      });
    },
    /** Mobile: hide legend + strip when the first three months have no milestones; desktop unchanged. */
    showMobileTimelineDesktopMessageOnly() {
      return this.isMobileRoadmap && !this.hasAnyEligibleEventInFirstThreeWindowMonths;
    },
    /** Full six months on ≥sm; on mobile, months from today through the month of the first event (min. one month). */
    displayMonthDataSet() {
      const full = this.monthDataSet;
      if (!this.isMobileRoadmap) {
        return full;
      }
      const endExclusive = Math.max(1, this.firstEventWindowIndex + 1);
      return full.slice(0, endExclusive);
    },
    showDesktopOnlyMessage() {
      if (this.showMobileTimelineDesktopMessageOnly) return false;
      return this.isMobileRoadmap && this.displayMonthDataSet.length < 6;
    },
  },
  mounted() {
    if (typeof window === 'undefined') return;
    this.onWindowResize();
    window.addEventListener('resize', this.onWindowResize, { passive: true });
  },
  beforeUnmount() {
    if (typeof window === 'undefined') return;
    window.removeEventListener('resize', this.onWindowResize);
  },
  methods: {
    onWindowResize() {
      if (typeof window === 'undefined') return;
      this.windowWidth = window.innerWidth;
    },
    calculateMonths(targetDate) {
      const today = new Date();
      const target = new Date(targetDate);

      const yearDiff = target.getFullYear() - today.getFullYear();
      const monthDiff = target.getMonth() - today.getMonth();

      let monthsLeft = yearDiff * 12 + monthDiff;

      if (target.getDate() < today.getDate()) {
        monthsLeft -= 1;
      }

      return monthsLeft;
    },
    updateMaxYPos(valueMaxYPos) {
      if (valueMaxYPos > this.maxYPos) {
        this.maxYPos = valueMaxYPos;
      }
    },
  },
};
</script>
